import React from "react"
import Button from "@/components/common/Buttons/Button"
import { validateEmail } from "@/utils/utils"
import SingleSelect from "@/components/selectors/SingleSelect"
import { DROPDOWN_VARIANTS } from "@/components/common/Dropdown"
import { useRouter } from "next/router"
import CustomInput, { INPUT_VARIANT } from "@/components/common/CustomInput"
import { ICONS, INPUT_TYPE } from "@/constants"
import { useBranch } from "@/hooks/useBranch"
import FileInput from "@/components/Forms/FileInput"

const validationErrors = {
	pl: {
		required: "Pole wymagane",
		invalid: "Błędny adres email",
		minLength: "minimum 3 znaki",
	},
	en: {
		required: "Field required",
		invalid: "Invalid email address",
		minLength: "minimum 3 characters",
	},
	de: {
		required: "Feld erforderlich",
		invalid: "ungültige E-Mail-Adresse",
		minLength: "mindestens 3 Zeichen",
	},
}

function getIconBasedOnName(name) {
	switch (name) {
		case "name":
			return ICONS.NAME
		case "email":
			return ICONS.CONTACT
		default:
			return ICONS.LOUPE
	}
}

function groupItemsByPattern(items, pattern) {
	const groups = []
	let currentIndex = 0

	for (const count of pattern) {
		const group = items.slice(currentIndex, currentIndex + count)
		if (group.length) {
			groups.push(group)
		}
		currentIndex += count
	}

	return groups
}

export default function Form({
	formData,
	setFormData,
	inputs = [],
	submitButtonText = "",
	handleSubmit,
	LayoutWrapper = React.Fragment,
	groupingPattern = [],
}) {
	const { locale } = useRouter()
	const { options: branchesOptions } = useBranch()

	const handleInputChange = (e) => {
		const { name, value } = e.target

		setFormData((prevData) => prevData.map((item) => (item[name] !== undefined ? { ...item, [name]: value } : item)))
	}

	const groupedInputs = groupingPattern.length ? groupItemsByPattern(inputs, groupingPattern) : [inputs]

	return (
		<div className="form">
			<form onSubmit={handleSubmit}>
				<LayoutWrapper>
					{groupedInputs.map((group, groupIndex) => (
						<div key={groupIndex} className="form-row">
							{group.map((item, index) => {
								const id = item?.id || ""
								const name = item?.name || ""
								const placeholder = item?.placeholder || ""
								const required = item?.required
								const inputType = item?.inputType || ""
								const finalPlaceholder = required ? `${placeholder.toLowerCase()}*` : placeholder.toLowerCase()
								const isTextarea = inputType === INPUT_TYPE.TEXTAREA
								const isSelect = inputType === INPUT_TYPE.SELECT
								const isVoivodeship = inputType === INPUT_TYPE.VOIVODESHIP
								const isAttachment = inputType === INPUT_TYPE.ATTACHMENT
								const valueIndex = formData.findIndex((el) => {
									return Object.keys(el).includes(name)
								})
								const value = formData[valueIndex][name]

								const selectOptions = item?.selectOptionsChannel || []
								const selectedOptionObject = selectOptions.find((option) => option.value === formData[index][name])

								const selectedBranch = branchesOptions.find((option) => option.value === formData[index][name])

								return (
									<React.Fragment key={id}>
										{isSelect && (
											<SingleSelect
												key={id}
												name={name}
												options={selectOptions}
												selectedValue={selectedOptionObject}
												onChange={handleInputChange}
												placeholder={placeholder}
												variant={DROPDOWN_VARIANTS.TERTIARY}
											/>
										)}

										{isVoivodeship && (
											<SingleSelect
												key={id}
												name={name}
												options={branchesOptions}
												selectedValue={selectedBranch}
												onChange={handleInputChange}
												placeholder={placeholder}
												variant={DROPDOWN_VARIANTS.TERTIARY}
											/>
										)}

										{isAttachment && (
											<FileInput key={id} name={name} index={index} formData={formData} setFormData={setFormData} />
										)}

										{!isSelect && !isVoivodeship && !isAttachment && (
											<CustomInput
												key={id}
												isTextarea={isTextarea}
												placeholder={finalPlaceholder}
												type={inputType}
												name={name}
												maxlength={isTextarea ? 512 : 255}
												required={required}
												onChange={handleInputChange}
												validate={
													inputType === "email"
														? validateEmail
														: (value) => value && value.trim() !== "" && value.trim().length >= 3
												}
												errorMessage={
													inputType === "email"
														? `${validationErrors[locale].invalid}`
														: `${validationErrors[locale].required} (${validationErrors[locale].minLength})`
												}
												variant={INPUT_VARIANT.PRIMARY}
												icon={getIconBasedOnName(name)}
												value={value}
											/>
										)}
									</React.Fragment>
								)
							})}
						</div>
					))}
				</LayoutWrapper>
				<Button type="submit">{submitButtonText}</Button>
			</form>
		</div>
	)
}
